import { createApi } from '@reduxjs/toolkit/query/react';
import { createAuthBaseQuery } from '@/features/baseQuery';
import { msGraphApi } from '../msGraph/msGraphApi';

export const ASK_AI_AUDIT_ACTIONS = {
	save_workspace: 'saved_to_workspace',
	email_composer_opened: 'email_composer_opened',
	task_dialog_opened: 'task_dialog_opened',
	output_copied: 'output_copied',
	saved_as_note: 'saved_as_note',
	note_dialog_opened: 'note_dialog_opened',
};

export const askAiApi = createApi({
	reducerPath: 'askAiApi',
	tagTypes: ['ASK_AI_SESSION', 'ASK_AI_MESSAGES'],
	baseQuery: createAuthBaseQuery({ baseUrl: process.env.REACT_APP_ASK_AI_API }),
	endpoints: (builder) => ({
		createAskAiSession: builder.mutation({
			query: (body) => ({
				url: '/sessions',
				method: 'POST',
				body,
			}),
		}),
		getAskAiSession: builder.query({
			providesTags: (result, error, sessionId) => [{ type: 'ASK_AI_SESSION', id: sessionId }],
			query: (sessionId) => ({
				url: `/sessions/${sessionId}`,
				method: 'GET',
			}),
			transformResponse: (response) => response?.session ?? null,
		}),
		getAskAiSessionMessages: builder.query({
			providesTags: (result, error, sessionId) => [
				{ type: 'ASK_AI_MESSAGES', id: sessionId },
			],
			query: (sessionId) => ({
				url: `/sessions/${sessionId}/messages`,
				method: 'GET',
			}),
			transformResponse: (response) => response?.messages ?? [],
		}),
		sendAskAiMessage: builder.mutation({
			invalidatesTags: (result, error, { sessionId }) => [
				{ type: 'ASK_AI_MESSAGES', id: sessionId },
			],
			query: ({ sessionId, message, preloadedSources = [] }) => ({
				url: `/sessions/${sessionId}/messages`,
				method: 'POST',
				body: { message, preloadedSources },
			}),
			transformResponse: (response) => response?.message ?? null,
		}),
		uploadAskAiDocument: builder.mutation({
			query: ({ sessionId, ...body }) => ({
				url: `/sessions/${sessionId}/uploads`,
				method: 'POST',
				body,
			}),
			transformResponse: (response) => response?.attachment ?? null,
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				try {
					await queryFulfilled;
					if (arg.sharepointItemId && arg.sharepointDriveId) {
						const { sharepointItemId: itemId, sharepointDriveId: driveId } = arg;
						dispatch(
							msGraphApi.util.invalidateTags([
								{ type: 'LIST_ITEM', id: `${driveId}-${itemId}` },
								{ type: 'ITEM', id: itemId },
								{ type: 'ITEMS', id: `${driveId}-${itemId}-recursive` },
								{ type: 'ITEMS', id: `${driveId}-${itemId}` },
							]),
						);
					}
				} catch (error) {
					console.error('Error uploading Ask AI document', error);
				}
			},
		}),
		createAskAiAudit: builder.mutation({
			query: ({ sessionId, ...body }) => ({
				url: `/sessions/${sessionId}/audit`,
				method: 'POST',
				body,
			}),
		}),
	}),
});

export const {
	useCreateAskAiSessionMutation,
	useGetAskAiSessionQuery,
	useLazyGetAskAiSessionMessagesQuery,
	useSendAskAiMessageMutation,
	useUploadAskAiDocumentMutation,
	useCreateAskAiAuditMutation,
} = askAiApi;
